import { marked } from 'marked';
import createDOMPurify from 'dompurify';

const DOMPurify = (typeof window !== 'undefined' && createDOMPurify && typeof createDOMPurify === 'function')
  ? createDOMPurify(window)
  : /** @type {any} */ (createDOMPurify);

if (DOMPurify && typeof DOMPurify.addHook === 'function') {
  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (node.tagName === 'A' && node.getAttribute && node.getAttribute('href')) {
      node.setAttribute('target', '_blank');
      node.setAttribute('rel', 'noopener noreferrer');
    }
  });
}

function autoLinkBareUrls(text) {
  const urlRegex = /(?<![\w@#])(https?:\/\/[^\s<>()]+[^\s.,;:!?<>()])/g;
  return text.replace(urlRegex, (url) => `[${url}](${url})`);
}

export function renderMarkdownToHtml(rawText, ext = 'md') {
  if (!rawText) return '';
  let source = rawText;
  if (ext === 'txt') {
    source = autoLinkBareUrls(source);
  }
  const html = ext === 'html' ? source : marked.parse(source, { gfm: true, breaks: true });
  return DOMPurify && typeof DOMPurify.sanitize === 'function' ? DOMPurify.sanitize(html) : html;
}

/** First project URL from markdown `[label](url)` or a bare https URL in the description. */
export function extractPrimaryUrl(text) {
  if (!text || typeof text !== 'string') return null;
  const md = text.match(/\[[^\]]*\]\((https?:\/\/[^)\s]+)\)/);
  if (md) return md[1];
  const bare = text.match(/(https?:\/\/[^\s<>[\]()]+)/);
  if (!bare) return null;
  return bare[1].replace(/[.,;:!?)]+$/, '');
}

/**
 * Pull Game / Date tags out of featured-work markdown.
 * Supports YAML frontmatter or leading `Game:` / `Date:` lines.
 */
export function extractWorkTags(rawText) {
  if (!rawText || typeof rawText !== 'string') {
    return { game: '', date: '', body: '' };
  }
  const parsed = parseFrontmatter(rawText);
  let game = typeof parsed.meta.game === 'string' ? parsed.meta.game.trim() : '';
  let date = typeof parsed.meta.date === 'string' ? parsed.meta.date.trim() : '';
  let body = parsed.body || '';

  while (true) {
    const match = body.match(/^(Game|Date):\s*(.+)\s*(?:\r?\n|$)/i);
    if (!match) break;
    const key = match[1].toLowerCase();
    const value = match[2].trim();
    if (key === 'game' && !game) game = value;
    if (key === 'date' && !date) date = value;
    body = body.slice(match[0].length).replace(/^\s*\r?\n/, '');
  }

  return { game, date, body };
}

export function parseFrontmatter(raw) {
  if (!raw || typeof raw !== 'string') return { meta: {}, body: '' };
  const match = raw.match(/^---\r?\n([\s\S]*?)\r?\n---\r?\n?([\s\S]*)$/);
  if (!match) return { meta: {}, body: raw.trim() };

  const meta = {};
  for (const line of match[1].split(/\r?\n/)) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    const idx = trimmed.indexOf(':');
    if (idx === -1) continue;
    const key = trimmed.slice(0, idx).trim();
    let value = trimmed.slice(idx + 1).trim();
    if (
      (value.startsWith('"') && value.endsWith('"')) ||
      (value.startsWith("'") && value.endsWith("'"))
    ) {
      value = value.slice(1, -1);
    }
    meta[key] = value;
  }

  return { meta, body: match[2].trim() };
}
